import React from 'react'
import { Box, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material'
import { AutoCoderPipeline } from '../../types/gotypes'
import {
  pipelineSwitchAvailability,
  pipelineSwitchState,
  showsPipelineSwitches,
} from '../../utils/pipelineSwitches'

/**
 * Coding as a REAL switch.
 *
 * It was a chip, sitting beside a read-only status chip that looked exactly the
 * same — so the only controls on the row read as labels and nobody found them.
 * A Switch says "I am a control and I have two positions" without a tooltip.
 *
 * It rendered Batching too, until T19 retired that gate: opening a record's
 * coding row and creating its batch are now one atomic write, so there was no
 * gated moment left for a Batching switch to guard. Coding is the money gate,
 * and switching it off is still how you stop spend now — submitted batches sit
 * unclaimed.
 *
 * ── Importing is deliberately NOT here. ──────────────────────────────────────
 *
 * These are the PIPELINE's gates. Importing is a data-collection concern —
 * "is the scheduled background feed running, and over what range?" — which
 * belongs to the feed, not to the pipeline. That separation is now real on the
 * backend too: the Sprinklr connector's channels are CODE (committed manifests)
 * and its cursor is the coverage frontier derived from the import receipts, so
 * the fusion this comment used to record as a violation is gone. What is left
 * on the pipeline row is the GENERAL connector gate — the Sprinklr loop no
 * longer reads it at all (it is armed by `PLATINUM_SPRINKLR_IMPORT_ENABLED`
 * plus the manifest's own `enabled`).
 *
 * Dropping the switch is safe ONLY because the backend was decoupled at the
 * same time: `ApplyLifecycle` no longer resets `connector_enabled`, so nothing
 * in this UI can flip a connector by accident. Previously it was reachable as a
 * side effect of Activate/Pause, which would have meant one Activate click
 * silently arming a pipeline's feed with nothing on screen able to stop it.
 *
 * Everything behind it is intact and still needed — the API's
 * `connector_enabled` field, `pipelineSwitches.ts`'s connector derivation and
 * `showsConnectorSwitch`. The control is expected to REAPPEAR on a
 * feeds/channels view (per feed, with its frontier and next window beside it),
 * or read-only here as "N feeds, M running". Until then the only writer is
 * `POST /pipelines/:id/flags` (`janitor sprinklr-provision --activate` retired
 * with the pipeline-as-connector shape; `janitor sprinklr-channels --check` is
 * its read-only successor).
 */
export type PipelineGate = 'connector' | 'coding'

export interface PipelineGateSwitchesProps {
  pipeline: AutoCoderPipeline
  onToggle: (pipeline: AutoCoderPipeline, gate: PipelineGate, next: boolean) => void
  /** 'compact' for a table row, 'full' for the page header. */
  size?: 'compact' | 'full'
  /** Blocks interaction while a flip is in flight. */
  busy?: boolean
}

const GATE_LABEL: Record<PipelineGate, string> = {
  connector: 'Importing',
  coding: 'Coding',
}

const GATE_HELP: Record<PipelineGate, { on: string; off: string }> = {
  connector: {
    on: 'Importing is ON — the scheduled connector loop may add new records to this pipeline.',
    off: 'Importing is OFF — no connector adds new records. Nothing already collected is lost.',
  },
  coding: {
    on: 'Coding is ON — records that enter this pipeline are coded. This spends money.',
    off: 'Coding is OFF — records that enter this pipeline sit uncoded and cost nothing until you switch it back on.',
  },
}

const PipelineGateSwitches: React.FC<PipelineGateSwitchesProps> = ({
  pipeline,
  onToggle,
  size = 'compact',
  busy = false,
}) => {
  if (!showsPipelineSwitches(pipeline)) return null

  const state = pipelineSwitchState(pipeline)
  const compact = size === 'compact'
  const values: Record<PipelineGate, boolean> = {
    connector: state.connectorEnabled,
    coding: state.codingEnabled,
  }

  // Coding is never disabled — the dispatcher pays no attention to lifecycle,
  // so switching it off must always be possible. T19 (2026-08-12) retired the
  // batching gate this used to render beside it.
  //
  // The pipeline's own gate, and nothing else — see the note above on why
  // Importing is not rendered here.
  const gates: PipelineGate[] = ['coding']
  const availabilities = gates.map(g => pipelineSwitchAvailability(pipeline, g))
  const notes = availabilities
    .map(a => a.note ?? a.warning)
    .filter((n): n is string => Boolean(n))

  const renderSwitch = (gate: PipelineGate) => {
    const checked = values[gate]
    const label = GATE_LABEL[gate]
    const availability = pipelineSwitchAvailability(pipeline, gate)
    const help = availability.note ?? availability.warning
      ?? (checked ? GATE_HELP[gate].on : GATE_HELP[gate].off)
    return (
      <Tooltip key={gate} title={help}>
        <FormControlLabel
          sx={{
            mr: compact ? 1 : 2,
            ml: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: compact ? '0.75rem' : '0.875rem',
              fontWeight: 600,
              color: availability.disabled ? 'text.disabled' : 'text.primary',
            },
          }}
          control={
            <Switch
              size={compact ? 'small' : 'medium'}
              color="info"
              checked={checked}
              disabled={availability.disabled || busy}
              // The dialog owns the actual flip: it states the consequence and
              // only then calls the API. This just proposes the direction.
              onChange={() => onToggle(pipeline, gate, !checked)}
            />
          }
          label={label}
        />
      </Tooltip>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0 : 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        {gates.map(renderSwitch)}
      </Box>
      {notes.map(note => (
        <Typography
          key={note}
          variant="caption"
          sx={{ color: 'warning.main', fontSize: compact ? '0.65rem' : '0.75rem', lineHeight: 1.3 }}
        >
          {note}
        </Typography>
      ))}
    </Box>
  )
}

export default PipelineGateSwitches
